import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, DatePicker, message } from "antd";
import {
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../utils/axios.config";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import locale from "antd/es/date-picker/locale/en_US";
import { format } from "date-fns";
import Swal from "sweetalert2";
import logger from "../../utils/logger";

// تفعيل plugin للتعامل مع التنسيقات المخصصة
dayjs.extend(customParseFormat);

const { Option } = Select;
const { TextArea } = Input;

const EditPaymentModal = ({ visible, onClose, onSuccess, payment }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [formData, setFormData] = useState({
    date: null,
  });

  // Fetch clients and cases
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsResponse, casesResponse] = await Promise.all([
          axiosInstance.get("/clients"),
          axiosInstance.get("/cases"),
        ]);

        if (clientsResponse.data?.data) {
          setClients(clientsResponse.data.data);
        }

        if (casesResponse.data?.data) {
          setAllCases(casesResponse.data.data);
        }
      } catch (error) {
        logger.error("Error fetching data:", error);
        message.error("خطأ في جلب البيانات");
      }
    };

    if (visible) {
      fetchData();
    }
  }, [visible]);

  // تحديث النموذج عند تغيير بيانات الدفعة
  useEffect(() => {
    if (payment && visible && clients.length > 0) {
      logger.debug("Loading payment data:", payment);
      logger.debug("Available clients:", clients);
      
      // استخدام الحقل الصحيح للتاريخ (date أو payment_date)
      const paymentDateValue = payment.date || payment.payment_date;
      const paymentDate = paymentDateValue ? dayjs(paymentDateValue) : null;
      
      // استخدام الحقل الصحيح للنوع (type أو payment_type)
      const paymentType = payment.type || payment.payment_type || "case";

      // إيجاد client_id من client_name إذا لم يكن موجوداً
      let clientId = payment.client_id;
      if (!clientId && payment.client_name) {
        const foundClient = clients.find(
          (c) => c.name === payment.client_name
        );
        clientId = foundClient?.id;
        logger.debug("Found client by name:", foundClient);
      }

      // إيجاد case_id من case_number إذا لم يكن موجوداً
      let caseId = payment.case_id;
      if (!caseId && payment.case_number && allCases.length > 0) {
        const foundCase = allCases.find(
          (c) => c.case_number === payment.case_number
        );
        caseId = foundCase?.id;
        logger.debug("Found case by number:", foundCase);
      }

      logger.debug("Payment date value:", paymentDateValue);
      logger.debug("Parsed date:", paymentDate);
      logger.debug("Client ID:", clientId);
      logger.debug("Case ID:", caseId);

      setFormData({
        date: paymentDate,
      });

      // تعيين القيم في النموذج
      const formValues = {
        client_id: clientId,
        case_id: caseId,
        amount: payment.amount,
        date: paymentDate,
        type: paymentType,
        status: payment.status || "completed",
        description: payment.description || "",
      };

      logger.debug("Setting form values:", formValues);
      form.setFieldsValue(formValues);

      // Filter cases for the selected client بعد تحميل القضايا
      if (clientId && allCases.length > 0) {
        setTimeout(() => {
          handleClientChange(clientId);
          // إعادة تعيين القضية بعد الفلترة
          if (caseId) {
            form.setFieldsValue({ case_id: caseId });
          }
        }, 100);
      }
    }
  }, [payment, visible, form, allCases, clients]);

  // Handle client selection
  const handleClientChange = (value) => {
    // لا نقوم بمسح case_id إذا كنا نقوم بالتحميل الأولي
    if (!payment || payment.client_id !== value) {
      form.setFieldsValue({ case_id: undefined });
    }

    if (!value) {
      setFilteredCases([]);
      return;
    }

    const clientId = value;
    const selectedClient = clients.find((c) => c.id === parseInt(clientId));

    // Filter cases for the selected client
    if (clientId && selectedClient) {
      const clientCases = allCases.filter(
        (caseItem) =>
          caseItem.client_id === parseInt(clientId) ||
          caseItem.client_name === selectedClient.name
      );
      setFilteredCases(clientCases);
    } else {
      setFilteredCases([]);
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      date: date,
    }));
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const paymentData = {
        amount: parseFloat(values.amount || 0).toFixed(2),
        payment_date: values.date
          ? dayjs(values.date).format("YYYY-MM-DD")
          : format(new Date(), "yyyy-MM-dd"),
        payment_type: values.type || "other",
        case_id: values.type === "case" ? values.case_id : null,
        client_id: values.client_id,
        description: values.description || "",
        status: values.status || "pending",
      };

      const response = await axiosInstance.put(
        `/payments/${payment.id}`,
        paymentData
      );

      if (response.data.success) {
        // Show success message with SweetAlert2
        await Swal.fire({
          title: "تم التعديل!",
          text: "تم تعديل بيانات الدفعة بنجاح",
          icon: "success",
          timer: 1500,
        });

        form.resetFields();
        onSuccess({
          success: true,
          data: {
            id: payment.id,
            ...paymentData,
          },
        });
        onClose();
      }
    } catch (error) {
      logger.error("Payment update error:", error);

      // Show error message with SweetAlert2
      Swal.fire({
        title: "خطأ!",
        text:
          error.response?.data?.message ||
          error.message ||
          "فشل في تعديل بيانات الدفعة",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const values = form.getFieldsValue();

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-right text-gray-800 mb-4">
          تعديل بيانات الدفعة
        </div>
      }
      open={visible}
      onCancel={onClose}
      okText="حفظ التغييرات"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={loading}
      width="95%"
      style={{ maxWidth: "800px" }}
      dir="rtl"
      className="custom-modal responsive-modal"
      styles={{
        body: { maxHeight: "80vh", overflow: "auto", padding: "15px" },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="px-2 md:px-4"
        requiredMark={false}
        dir="rtl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
          {/* Client Selection */}
          <Form.Item
            name="client_id"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                اسم العميل <span className="text-red-500">*</span>
              </span>
            }
            rules={[{ required: true, message: "الرجاء اختيار العميل" }]}
            className="mb-2 md:mb-3"
          >
            <Select
              showSearch
              placeholder="اختر العميل"
              optionFilterProp="children"
              onChange={handleClientChange}
              className="text-right h-10 md:h-11"
              size="middle"
              filterOption={(input, option) =>
                (option?.children ?? "").includes(input)
              }
            >
              {clients.map((client) => (
                <Option key={client.id} value={client.id}>
                  {client.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Case Selection */}
          <Form.Item
            name="case_id"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                اختر القضية
              </span>
            }
            className="mb-2 md:mb-3"
          >
            <Select
              showSearch
              placeholder={
                values.client_id
                  ? "-- اختر القضية --"
                  : "-- اختر عميل أولاً --"
              }
              optionFilterProp="children"
              disabled={!values.client_id || values.type !== "case"}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              className="text-right h-10 md:h-11"
              size="middle"
              options={filteredCases.map((caseItem) => ({
                value: caseItem.id,
                label: `${caseItem.case_number} - ${
                  caseItem.title || caseItem.case_type
                }`,
              }))}
              notFoundContent={
                values.client_id
                  ? "لا توجد قضايا لهذا العميل"
                  : "اختر عميل أولاً"
              }
            />
          </Form.Item>

          {/* Amount */}
          <Form.Item
            name="amount"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                المبلغ <span className="text-red-500">*</span>
              </span>
            }
            rules={[
              { required: true, message: "المبلغ مطلوب" },
              {
                type: "number",
                min: 1,
                transform: (value) => Number(value),
                message: "المبلغ يجب أن يكون أكبر من صفر",
              },
            ]}
            className="mb-2 md:mb-3"
          >
            <Input
              type="number"
              placeholder="أدخل المبلغ"
              className="text-right h-10 md:h-11"
              size="middle"
              suffix="د.ع"
            />
          </Form.Item>

          {/* Date */}
          <Form.Item
            name="date"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                التاريخ
              </span>
            }
            rules={[{ required: true, message: "التاريخ مطلوب" }]}
            className="mb-2 md:mb-3"
          >
            <DatePicker
              placeholder="اختر التاريخ"
              style={{ width: "100%" }}
              format="YYYY/MM/DD"
              locale={locale}
              className="text-right h-10 md:h-11"
              size="middle"
              value={formData.date}
              onChange={(date) => {
                setFormData((prev) => ({
                  ...prev,
                  date: date,
                }));
                form.setFieldsValue({
                  date: date,
                });
              }}
              suffixIcon={<CalendarOutlined className="text-primary" />}
              styles={{
                popup: {
                  zIndex: 1060,
                },
              }}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </Form.Item>

          {/* Payment Type */}
          <Form.Item
            name="type"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                نوع الدفعة
              </span>
            }
            rules={[{ required: true, message: "نوع الدفعة مطلوب" }]}
            className="mb-2 md:mb-3"
          >
            <Select
              placeholder="اختر النوع"
              className="text-right h-10 md:h-11"
              size="middle"
            >
              <Option value="case">قضية</Option>
              <Option value="consultation">استشارة</Option>
              <Option value="other">أخرى</Option>
            </Select>
          </Form.Item>

          {/* Payment Status */}
          <Form.Item
            name="status"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                الحالة <span className="text-red-500">*</span>
              </span>
            }
            rules={[{ required: true, message: "حالة الدفعة مطلوبة" }]}
            className="mb-2 md:mb-3"
          >
            <Select
              placeholder="اختر الحالة"
              className="text-right h-10 md:h-11"
              size="middle"
            >
              <Option value="completed">مكتملة</Option>
              <Option value="pending">معلقة</Option>
            </Select>
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                الوصف
              </span>
            }
            className="mb-2 md:mb-3 md:col-span-2"
          >
            <TextArea
              placeholder="وصف اختياري للدفعة..."
              className="text-right"
              rows={4}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default EditPaymentModal;
